import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  DocumentReference,
  type DocumentData,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import { PdHacksErrorConverter } from '@core/services/pd-hacks/converters/pd-hacks-error-converter'
import { fromFirestorePdHackCategory } from '@core/services/pd-hacks/converters/pd-hack-category-converter'
import {
  fromFirestorePdSymptom,
  toFirestorePdSymptomPatch,
} from '@core/services/pd-hacks/converters/pd-symptom-converter'
import { fromFirestoreWhatWork } from '@core/services/pd-hacks/converters/what-work-converter'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type {
  CreateHackInput,
  PdHackCategory,
  CreateWhatWorkInput,
  PdSymptom,
  UpdateHackPatch,
  UpdateWhatWorkPatch,
  WhatWork,
} from '@domain/models/pd-hacks'

const PD_SYMPTOMS_COLLECTION = 'pd_symptoms'
const PD_HACKS_CATEGORIES_COLLECTION = 'pd_hacks_categories'
const WHAT_WORKS_SUBCOLLECTION = 'what_works'
const ACTIVITY_COMMENTS_COLLECTION = 'activity_comments'
const USERS_COLLECTION = 'users'

function getPdSymptomsCollection() {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }

  return collection(firestore, PD_SYMPTOMS_COLLECTION)
}

function getPdHackCategoriesCollection() {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }

  return collection(firestore, PD_HACKS_CATEGORIES_COLLECTION)
}

function getPdSymptomDoc(symptomId: string) {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }

  return doc(firestore, PD_SYMPTOMS_COLLECTION, symptomId)
}

function getWhatWorksCollection(symptomId: string) {
  return collection(getPdSymptomDoc(symptomId), WHAT_WORKS_SUBCOLLECTION)
}

function getWhatWorkDoc(symptomId: string, whatWorkId: string) {
  return doc(getWhatWorksCollection(symptomId), whatWorkId)
}

function getActivityCommentsCollection() {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }

  return collection(firestore, ACTIVITY_COMMENTS_COLLECTION)
}

function getUserDoc(userId: string) {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }

  return doc(firestore, USERS_COLLECTION, userId)
}

function hasUserReference(
  input: unknown,
  userId: string,
): boolean {
  if (!Array.isArray(input)) {
    return false
  }

  return input.some((item) => {
    if (!item || typeof item !== 'object') {
      return false
    }

    const maybeRef = item as { id?: unknown }
    return typeof maybeRef.id === 'string' && maybeRef.id === userId
  })
}

function validateRequiredText(value: string, fieldLabel: string): string {
  const normalized = value.trim()
  if (normalized.length === 0) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: `${fieldLabel} is required.`,
    })
  }
  return normalized
}

function toOptionalTrimmedString(value: string): string | undefined {
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : undefined
}

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  return undefined
}

export class PdHacksService {
  private static instance: PdHacksService

  private constructor() {}

  public static getInstance(): PdHacksService {
    if (!PdHacksService.instance) {
      PdHacksService.instance = new PdHacksService()
    }
    return PdHacksService.instance
  }

  public async fetchHackSymptoms(pdHacksCategoryId?: string): Promise<PdSymptom[]> {
    try {
      const normalizedCategoryId = pdHacksCategoryId?.trim()
      const hacksQuery = query(
        getPdSymptomsCollection(),
        where('michels_pd_hacks_video_url', '!=', null),
      )
      const snapshot = await getDocs(hacksQuery)
      return snapshot.docs
        .map((item) => fromFirestorePdSymptom(item))
        .filter((symptom) =>
          Boolean(symptom.michelsPdHacksVideoUrl?.trim()),
        )
        .filter((symptom) => {
          if (!normalizedCategoryId) {
            return true
          }
          return symptom.pdHacksCategoryId === normalizedCategoryId
        })
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async fetchPdHackCategories(activeOnly = true): Promise<PdHackCategory[]> {
    try {
      const snapshot = await getDocs(
        query(getPdHackCategoriesCollection(), orderBy('name', 'asc')),
      )
      const categories = snapshot.docs.map((item) => fromFirestorePdHackCategory(item))
      return activeOnly ? categories.filter((item) => item.isActive) : categories
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async fetchAllSymptoms(): Promise<PdSymptom[]> {
    try {
      const snapshot = await getDocs(
        query(getPdSymptomsCollection(), orderBy('name', 'asc')),
      )
      return snapshot.docs.map((item) => fromFirestorePdSymptom(item))
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async fetchSymptom(symptomId: string): Promise<PdSymptom> {
    try {
      const snapshot = await getDoc(getPdSymptomDoc(symptomId))
      if (!snapshot.exists()) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: `Symptom "${symptomId}" was not found.`,
        })
      }
      return fromFirestorePdSymptom(snapshot)
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async createSymptom(input: CreateHackInput): Promise<string> {
    try {
      const payload = toFirestorePdSymptomPatch({
        name: input.name,
        image: input.image,
        createdTime: new Date(),
        solutionCount: input.solutionCount ?? 0,
        michelsPdHacksVideoUrl: input.michelsPdHacksVideoUrl,
        pdHacksCategoryId: input.pdHacksCategoryId,
      })
      const createdDoc = await addDoc(getPdSymptomsCollection(), payload)
      return createdDoc.id
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async updateSymptom(
    symptomId: string,
    patch: UpdateHackPatch,
  ): Promise<void> {
    try {
      const normalizedPdHacksCategoryId = patch.pdHacksCategoryId?.trim()
      const payload = toFirestorePdSymptomPatch({
        name: patch.name,
        image: patch.image,
        solutionCount: patch.solutionCount,
        michelsPdHacksVideoUrl: patch.michelsPdHacksVideoUrl,
        pdHacksCategoryId: normalizedPdHacksCategoryId,
      })

      if (patch.pdHacksCategoryId !== undefined && !normalizedPdHacksCategoryId) {
        payload['pd_hacks_category_id'] = deleteField()
      }

      if (Object.keys(payload).length === 0) {
        return
      }

      await updateDoc(getPdSymptomDoc(symptomId), payload)
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async clearSymptomHackVideo(symptomId: string): Promise<void> {
    try {
      await updateDoc(getPdSymptomDoc(symptomId), {
        michels_pd_hacks_video_url: deleteField(),
      })
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async fetchWhatWorks(symptomId: string): Promise<WhatWork[]> {
    try {
      const snapshot = await getDocs(
        query(getWhatWorksCollection(symptomId), orderBy('created_time', 'desc')),
      )
      return snapshot.docs.map((item) => fromFirestoreWhatWork(item))
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async fetchWhatWork(
    symptomId: string,
    whatWorkId: string,
  ): Promise<WhatWork> {
    try {
      const snapshot = await getDoc(getWhatWorkDoc(symptomId, whatWorkId))
      if (!snapshot.exists()) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: `What works "${whatWorkId}" was not found.`,
        })
      }
      return fromFirestoreWhatWork(snapshot)
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async fetchWhatWorksCommentCount(
    symptomId: string,
    whatWorkId: string,
  ): Promise<number> {
    try {
      const countQuery = query(
        getActivityCommentsCollection(),
        where('solution_ref', '==', getWhatWorkDoc(symptomId, whatWorkId)),
      )
      const countSnapshot = await getCountFromServer(countQuery)
      return countSnapshot.data().count
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async fetchWhatWorksCommentCounts(
    symptomId: string,
    whatWorkIds: string[],
  ): Promise<Record<string, number>> {
    const uniqueIds = Array.from(
      new Set(whatWorkIds.map((item) => item.trim()).filter(Boolean)),
    )

    if (uniqueIds.length === 0) {
      return {}
    }

    const counts = await Promise.all(
      uniqueIds.map(async (whatWorkId) => [
        whatWorkId,
        await this.fetchWhatWorksCommentCount(symptomId, whatWorkId),
      ] as const),
    )

    return Object.fromEntries(counts)
  }

  public async fetchLatestWhatWorkCreatedTime(
    symptomId: string,
  ): Promise<Date | undefined> {
    try {
      const snapshot = await getDocs(
        query(
          getWhatWorksCollection(symptomId),
          orderBy('created_time', 'desc'),
          limit(1),
        ),
      )
      if (snapshot.empty) {
        return undefined
      }

      const latest = fromFirestoreWhatWork(snapshot.docs[0])
      return latest.createdTime
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  private async fetchLatestWhatWorkCommentPostedOn(
    symptomId: string,
    whatWorkId: string,
  ): Promise<Date | undefined> {
    const snapshot = await getDocs(
      query(
        getActivityCommentsCollection(),
        where('solution_ref', '==', getWhatWorkDoc(symptomId, whatWorkId)),
        orderBy('posted_on', 'desc'),
        limit(1),
      ),
    )

    if (snapshot.empty) {
      return undefined
    }

    const data = snapshot.docs[0].data() as Record<string, unknown>
    return toOptionalDate(data['posted_on'])
  }

  public async fetchLatestSymptomCommentPostedOn(
    symptomId: string,
  ): Promise<Date | undefined> {
    try {
      const whatWorksSnapshot = await getDocs(getWhatWorksCollection(symptomId))
      if (whatWorksSnapshot.empty) {
        return undefined
      }

      const whatWorkIds = whatWorksSnapshot.docs.map((item) => item.id)
      const commentDates = await Promise.all(
        whatWorkIds.map((whatWorkId) =>
          this.fetchLatestWhatWorkCommentPostedOn(symptomId, whatWorkId),
        ),
      )

      return commentDates.reduce<Date | undefined>((latest, current) => {
        if (!current) {
          return latest
        }
        if (!latest) {
          return current
        }
        return current.getTime() > latest.getTime() ? current : latest
      }, undefined)
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async createWhatWork(
    symptomId: string,
    input: CreateWhatWorkInput,
    userId: string,
  ): Promise<string> {
    try {
      const solution = validateRequiredText(input.solution, 'Solution')
      const description =
        input.description !== undefined
          ? toOptionalTrimmedString(input.description)
          : undefined
      const userRef = getUserDoc(userId) as DocumentReference<DocumentData>
      const payload: Record<string, unknown> = {
        solution,
        added_by: userRef,
        created_time: new Date(),
        total_votes: 0,
        voted_by: [],
        positive_ratings: [],
        negative_ratings: [],
        symptom_id: symptomId,
      }
      if (description !== undefined) {
        payload['description'] = description
      }
      const createdDoc = await addDoc(getWhatWorksCollection(symptomId), payload)
      return createdDoc.id
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async updateWhatWork(
    symptomId: string,
    whatWorkId: string,
    patch: UpdateWhatWorkPatch,
  ): Promise<void> {
    try {
      const payload: Record<string, unknown> = {}

      if (patch.solution !== undefined) {
        payload['solution'] = validateRequiredText(patch.solution, 'Solution')
      }

      if (patch.description !== undefined) {
        const description = toOptionalTrimmedString(patch.description)
        payload['description'] = description !== undefined ? description : deleteField()
      }

      if (Object.keys(payload).length === 0) {
        return
      }

      await updateDoc(getWhatWorkDoc(symptomId, whatWorkId), payload)
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async deleteWhatWork(
    symptomId: string,
    whatWorkId: string,
  ): Promise<void> {
    try {
      await deleteDoc(getWhatWorkDoc(symptomId, whatWorkId))
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }

  public async voteWhatWork(
    symptomId: string,
    whatWorkId: string,
    userId: string,
    isPositive: boolean,
  ): Promise<{ totalVotes: number; userVote: 'up' | 'down' | null }> {
    if (!firestore) {
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Firestore is not initialized.',
      })
    }

    try {
      const whatWorkRef = getWhatWorkDoc(symptomId, whatWorkId)
      const userRef = getUserDoc(userId) as DocumentReference<DocumentData>

      return await runTransaction(firestore, async (transaction) => {
        const snapshot = await transaction.get(whatWorkRef)
        if (!snapshot.exists()) {
          throw new CoreError({
            cause: CoreErrorCause.DataNotFound,
            context: `What works "${whatWorkId}" was not found.`,
          })
        }

        const data = snapshot.data() as Record<string, unknown>
        const hasPositive = hasUserReference(data['positive_ratings'], userId)
        const hasNegative = hasUserReference(data['negative_ratings'], userId)
        const currentTotalVotes =
          typeof data['total_votes'] === 'number' ? data['total_votes'] : 0

        if (isPositive) {
          if (hasPositive) {
            transaction.update(whatWorkRef, {
              total_votes: currentTotalVotes - 1,
              voted_by: arrayRemove(userRef),
              positive_ratings: arrayRemove(userRef),
            })
            return { totalVotes: currentTotalVotes - 1, userVote: null }
          }

          if (hasNegative) {
            transaction.update(whatWorkRef, {
              total_votes: currentTotalVotes + 2,
              voted_by: arrayUnion(userRef),
              positive_ratings: arrayUnion(userRef),
              negative_ratings: arrayRemove(userRef),
            })
            return { totalVotes: currentTotalVotes + 2, userVote: 'up' }
          }

          transaction.update(whatWorkRef, {
            total_votes: currentTotalVotes + 1,
            voted_by: arrayUnion(userRef),
            positive_ratings: arrayUnion(userRef),
          })
          return { totalVotes: currentTotalVotes + 1, userVote: 'up' }
        }

        if (hasNegative) {
          transaction.update(whatWorkRef, {
            total_votes: currentTotalVotes + 1,
            voted_by: arrayRemove(userRef),
            negative_ratings: arrayRemove(userRef),
          })
          return { totalVotes: currentTotalVotes + 1, userVote: null }
        }

        if (hasPositive) {
          transaction.update(whatWorkRef, {
            total_votes: currentTotalVotes - 2,
            voted_by: arrayUnion(userRef),
            positive_ratings: arrayRemove(userRef),
            negative_ratings: arrayUnion(userRef),
          })
          return { totalVotes: currentTotalVotes - 2, userVote: 'down' }
        }

        transaction.update(whatWorkRef, {
          total_votes: currentTotalVotes - 1,
          voted_by: arrayUnion(userRef),
          negative_ratings: arrayUnion(userRef),
        })
        return { totalVotes: currentTotalVotes - 1, userVote: 'down' }
      })
    } catch (error) {
      throw PdHacksErrorConverter.domainModel(error)
    }
  }
}

const pdHacksService = PdHacksService.getInstance()

export async function fetchHackSymptoms(pdHacksCategoryId?: string): Promise<PdSymptom[]> {
  return pdHacksService.fetchHackSymptoms(pdHacksCategoryId)
}

export async function fetchPdHackCategories(activeOnly = true): Promise<PdHackCategory[]> {
  return pdHacksService.fetchPdHackCategories(activeOnly)
}

export async function fetchAllSymptoms(): Promise<PdSymptom[]> {
  return pdHacksService.fetchAllSymptoms()
}

export async function fetchSymptom(symptomId: string): Promise<PdSymptom> {
  return pdHacksService.fetchSymptom(symptomId)
}

export async function createSymptom(input: CreateHackInput): Promise<string> {
  return pdHacksService.createSymptom(input)
}

export async function updateSymptom(
  symptomId: string,
  patch: UpdateHackPatch,
): Promise<void> {
  return pdHacksService.updateSymptom(symptomId, patch)
}

export async function clearSymptomHackVideo(symptomId: string): Promise<void> {
  return pdHacksService.clearSymptomHackVideo(symptomId)
}

export async function fetchWhatWorks(symptomId: string): Promise<WhatWork[]> {
  return pdHacksService.fetchWhatWorks(symptomId)
}

export async function fetchWhatWork(
  symptomId: string,
  whatWorkId: string,
): Promise<WhatWork> {
  return pdHacksService.fetchWhatWork(symptomId, whatWorkId)
}

export async function fetchWhatWorksCommentCount(
  symptomId: string,
  whatWorkId: string,
): Promise<number> {
  return pdHacksService.fetchWhatWorksCommentCount(symptomId, whatWorkId)
}

export async function fetchWhatWorksCommentCounts(
  symptomId: string,
  whatWorkIds: string[],
): Promise<Record<string, number>> {
  return pdHacksService.fetchWhatWorksCommentCounts(symptomId, whatWorkIds)
}

export async function fetchLatestWhatWorkCreatedTime(
  symptomId: string,
): Promise<Date | undefined> {
  return pdHacksService.fetchLatestWhatWorkCreatedTime(symptomId)
}

export async function fetchLatestSymptomCommentPostedOn(
  symptomId: string,
): Promise<Date | undefined> {
  return pdHacksService.fetchLatestSymptomCommentPostedOn(symptomId)
}

export async function voteWhatWork(
  symptomId: string,
  whatWorkId: string,
  userId: string,
  isPositive: boolean,
): Promise<{ totalVotes: number; userVote: 'up' | 'down' | null }> {
  return pdHacksService.voteWhatWork(symptomId, whatWorkId, userId, isPositive)
}

export async function createWhatWork(
  symptomId: string,
  input: CreateWhatWorkInput,
  userId: string,
): Promise<string> {
  return pdHacksService.createWhatWork(symptomId, input, userId)
}

export async function updateWhatWork(
  symptomId: string,
  whatWorkId: string,
  patch: UpdateWhatWorkPatch,
): Promise<void> {
  return pdHacksService.updateWhatWork(symptomId, whatWorkId, patch)
}

export async function deleteWhatWork(
  symptomId: string,
  whatWorkId: string,
): Promise<void> {
  return pdHacksService.deleteWhatWork(symptomId, whatWorkId)
}
